import { dehydrate, hydrate, type DehydratedState, type Query, type QueryClient } from '@tanstack/react-query'

/**
 * Чтение без сети (этап 4, PWA): кэш react-query нового интерфейса
 * сохраняется на устройстве и поднимается при запуске.
 *
 * Политика хранения (ТЗ стр. 24: «чтение кэша только при безопасной политике»):
 * - снимок привязан к владельцу - пользователь, клуб и направление; чужой
 *   снимок не поднимается и стирается (общее устройство, смена аккаунта);
 * - выход из аккаунта и потеря входа стирают снимок (это делает PWA);
 * - запись старше 7 дней не поднимается и не сохраняется;
 * - сохраняются только разделы из списка PERSISTED: то, что нужно в зале.
 *   Штаб (история, массовые действия), команда, каналы и подписка не
 *   сохраняются: их без сети не читают, а массовое действие должно быть свежим;
 * - только успешные ответы: ошибка «нет доступа» не оставляет прежних данных.
 *
 * Хранилище - IndexedDB: запись асинхронная (не тормозит экран на каждом
 * сохранении), не делит 5 МБ localStorage с токенами входа и не требует
 * JSON. Если IndexedDB недоступна (приватный режим), приложение работает как
 * раньше, просто без чтения без сети.
 */

export const OFFLINE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

const ROOT = 'redesign'

/** Разделы, которые сохраняются на устройстве (второй элемент ключа запроса). */
const PERSISTED = new Set([
  'context',
  'sessions',
  'board',
  'athletes',
  'athlete',
  'athlete-attendance',
  'athlete-notes',
  'groups',
  'group',
  'group-athletes',
  'journal',
  'payments',
  'schedule-timeline',
  'sport-fields',
  'sport-types',
  'club-overview',
  // Соревнования и сборы: список, карточка, участники, события спортсмена
  'events',
  'event',
  'event-participants',
  'athlete-events',
  // Записи групп и материалы направления (этап 5 шаг 3): записи спортсмена - выше
  'group-notes',
  'materials',
  'material',
])

/** Предел записей в снимке: старые вытесняются свежими. */
const MAX_QUERIES = 300

export interface CacheOwner {
  userId: string
  organizationId: string
  assistantId: string
}

export interface OfflineSnapshot {
  v: 1
  owner: CacheOwner
  /** Профиль пользователя: чтобы открыть приложение без сети, не спрашивая сервер. */
  user: unknown
  savedAt: number
  state: DehydratedState
}

export interface OfflineStore {
  read(): Promise<OfflineSnapshot | null>
  write(snapshot: OfflineSnapshot): Promise<void>
  clear(): Promise<void>
}

function sameOwner(a: CacheOwner, b: CacheOwner): boolean {
  return a.userId === b.userId && a.organizationId === b.organizationId && a.assistantId === b.assistantId
}

function isSnapshot(v: unknown): v is OfflineSnapshot {
  const s = v as OfflineSnapshot | null
  return !!s && s.v === 1 && !!s.owner && typeof s.savedAt === 'number' && Array.isArray(s.state?.queries)
}

// ---------- хранилища ----------

const DB_STORE = 'kv'
const DB_KEY = 'redesign-cache'

/**
 * Сколько ждать хранилище. Запуск приложения не должен зависеть от IndexedDB:
 * открытие может повиснуть (другая вкладка держит базу при удалении или
 * обновлении версии) - тогда работаем без снимка.
 */
const STORE_TIMEOUT_MS = 2000

function withTimeout<T>(p: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('indexedDB timeout')), STORE_TIMEOUT_MS)
    p.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      (e) => {
        clearTimeout(t)
        reject(e)
      },
    )
  })
}

function openDb(name: string, onClosed: () => void): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE)
    req.onsuccess = () => {
      const db = req.result
      // Базу удаляют или обновляют из другой вкладки: отпускаем, чтобы не держать их.
      db.onversionchange = () => {
        db.close()
        onClosed()
      }
      resolve(db)
    }
    req.onerror = () => reject(req.error)
    req.onblocked = () => reject(new Error('indexedDB blocked'))
  })
}

/**
 * Снимок в IndexedDB. Сбои и зависания хранилища не роняют и не держат
 * приложение: чтение - null, запись - тишина.
 */
export function createIndexedDbStore(name = 'procoach-offline'): OfflineStore {
  let db: Promise<IDBDatabase> | null = null
  const getDb = () => {
    if (typeof indexedDB === 'undefined') return Promise.reject(new Error('no indexedDB'))
    if (!db) {
      const opening = openDb(name, () => {
        if (db === opening) db = null
      })
      db = opening
      opening.catch(() => {
        if (db === opening) db = null
      })
    }
    return db
  }
  const run = <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
    withTimeout(
      getDb().then(
        (d) =>
          new Promise<T>((resolve, reject) => {
            const tx = d.transaction(DB_STORE, mode)
            const req = fn(tx.objectStore(DB_STORE))
            tx.oncomplete = () => resolve(req.result)
            tx.onerror = () => reject(tx.error)
            tx.onabort = () => reject(tx.error)
          }),
      ),
    )

  return {
    read: () =>
      run('readonly', (s) => s.get(DB_KEY)).then(
        (v) => (isSnapshot(v) ? v : null),
        () => null,
      ),
    write: (snapshot) =>
      run('readwrite', (s) => s.put(snapshot, DB_KEY)).then(
        () => undefined,
        () => undefined,
      ),
    clear: () =>
      run('readwrite', (s) => s.delete(DB_KEY)).then(
        () => undefined,
        () => undefined,
      ),
  }
}

/** Снимок в памяти: тесты и среды без IndexedDB. */
export function createMemoryStore(initial: OfflineSnapshot | null = null): OfflineStore & {
  snapshot: OfflineSnapshot | null
} {
  const store = {
    snapshot: initial,
    read: async () => (store.snapshot ? structuredCloneSafe(store.snapshot) : null),
    write: async (s: OfflineSnapshot) => {
      store.snapshot = structuredCloneSafe(s)
    },
    clear: async () => {
      store.snapshot = null
    },
  }
  return store
}

function structuredCloneSafe<T>(v: T): T {
  return typeof structuredClone === 'function' ? structuredClone(v) : (JSON.parse(JSON.stringify(v)) as T)
}

// ---------- что сохраняем ----------

export function shouldPersistQuery(query: Query, now: number): boolean {
  const key = query.queryKey
  if (key[0] !== ROOT || typeof key[1] !== 'string' || !PERSISTED.has(key[1])) return false
  const { status, data, dataUpdatedAt } = query.state
  return status === 'success' && data !== undefined && now - dataUpdatedAt < OFFLINE_MAX_AGE_MS
}

function freshQueries(state: DehydratedState, now: number) {
  return state.queries
    .filter((q) => {
      const key = q.queryKey
      return (
        key[0] === ROOT &&
        typeof key[1] === 'string' &&
        PERSISTED.has(key[1]) &&
        q.state.status === 'success' &&
        q.state.data !== undefined &&
        now - q.state.dataUpdatedAt < OFFLINE_MAX_AGE_MS
      )
    })
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    .slice(0, MAX_QUERIES)
    .map((q) => ({ ...q, state: { ...q.state, fetchStatus: 'idle' as const } }))
}

/**
 * Поднять сохранённое в кэш. Снимок другого владельца стирается. Возвращает
 * true, если что-то поднято.
 */
export async function restoreOfflineCache(
  client: QueryClient,
  store: OfflineStore,
  owner: CacheOwner,
  now = Date.now(),
): Promise<boolean> {
  const snap = await store.read()
  if (!snap) return false
  if (!sameOwner(snap.owner, owner)) {
    await store.clear()
    return false
  }
  const queries = freshQueries(snap.state, now)
  if (queries.length === 0) return false
  hydrate(client, { mutations: [], queries })
  return true
}

/**
 * Сохранять кэш при изменениях: не чаще раза в delayMs и сразу при уходе
 * приложения в фон (телефон могут убрать в карман и выгрузить). current()
 * говорит, чей сейчас кэш; null - владелец ещё не известен, не пишем.
 * Возвращает отписку.
 */
export function persistOfflineCache(
  client: QueryClient,
  store: OfflineStore,
  current: () => { owner: CacheOwner; user: unknown } | null,
  { delayMs = 1000, now = Date.now }: { delayMs?: number; now?: () => number } = {},
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined

  const flush = () => {
    clearTimeout(timer)
    timer = undefined
    const cur = current()
    if (!cur) return
    const t = now()
    const state = dehydrate(client, { shouldDehydrateQuery: (q) => shouldPersistQuery(q, t) })
    void store.write({ v: 1, owner: cur.owner, user: cur.user, savedAt: t, state: { mutations: [], queries: freshQueries(state, t) } })
  }

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] !== ROOT) return
    if (event.type === 'observerAdded' || event.type === 'observerRemoved' || event.type === 'observerOptionsUpdated') return
    if (timer === undefined) timer = setTimeout(flush, delayMs)
  })

  const onHide = () => {
    if (document.visibilityState === 'hidden' && timer !== undefined) flush()
  }
  const onPageHide = () => {
    if (timer !== undefined) flush()
  }
  document.addEventListener('visibilitychange', onHide)
  window.addEventListener('pagehide', onPageHide)

  return () => {
    clearTimeout(timer)
    timer = undefined
    unsubscribe()
    document.removeEventListener('visibilitychange', onHide)
    window.removeEventListener('pagehide', onPageHide)
  }
}

// ---------- запуск без сети ----------

/**
 * id пользователя из access-токена (JWT, поле user_id). Подпись не
 * проверяется - это не вход, а сверка: принадлежит ли сохранённый снимок
 * тому, чей токен лежит на устройстве. null - токен не разобрать.
 */
export function userIdFromToken(token: string | null): string | null {
  if (!token) return null
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')))
    return typeof json?.user_id === 'string' && json.user_id ? json.user_id : null
  } catch {
    return null
  }
}

/**
 * Сеанс для запуска без сети: профиль и клуб с направлением из снимка того же
 * пользователя, что в токене, не старше 7 дней. Иначе null.
 */
export async function readOfflineSession(
  store: OfflineStore,
  userId: string | null,
  now = Date.now(),
): Promise<{ user: unknown; owner: CacheOwner } | null> {
  if (!userId) return null
  const snap = await store.read()
  if (!snap || snap.owner.userId !== userId || now - snap.savedAt >= OFFLINE_MAX_AGE_MS) return null
  const user = snap.user as { id?: unknown } | null
  if (!user || user.id !== userId) return null
  return { user: snap.user, owner: snap.owner }
}

/** Итог запуска: чей профиль открываем и откуда он. */
export type BootResult =
  | { kind: 'online'; user: unknown }
  | { kind: 'offline'; user: unknown; owner: CacheOwner }
  /** Сервер не дал профиль, а снимка нет: входа не теряем, просим повторить. */
  | { kind: 'unavailable' }
  /** Сервер отказал во входе: токены и снимок надо стереть. */
  | { kind: 'signed_out' }

/**
 * Запуск PWA по сохранённому токену. Разлогиниваем только на настоящий отказ
 * сервера: 401 (интерцептор уже не смог обновить токен), 403, 404 (профиля
 * нет). Нет сети, сервер не отвечает или отвечает 5xx - вход остаётся, а
 * профиль, клуб и направление берутся из снимка того же пользователя, что в
 * токене. Ошибка - форма APIError интерцептора: { error, status? }.
 */
export async function bootSession(
  token: string,
  fetchMe: () => Promise<unknown>,
  store: OfflineStore,
  now = Date.now(),
): Promise<BootResult> {
  try {
    return { kind: 'online', user: await fetchMe() }
  } catch (err) {
    const status = (err as { status?: number } | null)?.status
    if (status === 401 || status === 403 || status === 404) return { kind: 'signed_out' }
    const session = await readOfflineSession(store, userIdFromToken(token), now)
    if (!session) return { kind: 'unavailable' }
    const user = session.user as Record<string, unknown>
    // Клуб в профиле мог устареть (переключали без перезагрузки профиля) - берём клуб снимка.
    return { kind: 'offline', user: { ...user, selected_organization_id: session.owner.organizationId }, owner: session.owner }
  }
}
